import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Shield, Eye, FolderX, Trash2, Plus } from 'lucide-react'
import {
  listAggregatorGrants,
  revokeAggregatorGrant,
  removeAggregatorFolderExclusion,
  setExtendedVisibility,
} from '../../services/api'
import type { AggregatorGrant } from '../../types'
import ConfirmDialog from '../shared/ConfirmDialog'
import GrantAggregatorAccessDialog from './GrantAggregatorAccessDialog'
import HideFolderPicker from './HideFolderPicker'

interface Props {
  projectId: string
}

export default function AggregatorSharingPanel({ projectId }: Props) {
  const queryClient = useQueryClient()

  const [grantDialogOpen, setGrantDialogOpen] = useState(false)
  const [hideFolderTarget, setHideFolderTarget] = useState<AggregatorGrant | null>(null)
  const [revokeTarget, setRevokeTarget] = useState<AggregatorGrant | null>(null)
  const [extVisConfirmTarget, setExtVisConfirmTarget] = useState<{
    grant: AggregatorGrant
    value: boolean
  } | null>(null)

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['aggregator-grants', projectId],
    queryFn: () => listAggregatorGrants(projectId),
  })

  const revokeMutation = useMutation({
    mutationFn: ({ aggregatorId }: { aggregatorId: string }) =>
      revokeAggregatorGrant(projectId, aggregatorId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['aggregator-grants', projectId] })
      queryClient.invalidateQueries({ queryKey: ['available-aggregators', projectId] })
      setRevokeTarget(null)
    },
  })

  const restoreFolderMutation = useMutation({
    mutationFn: ({ aggregatorId, folderId }: { aggregatorId: string; folderId: string }) =>
      removeAggregatorFolderExclusion(projectId, aggregatorId, folderId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['aggregator-grants', projectId] })
    },
  })

  const extVisMutation = useMutation({
    mutationFn: ({ entityId, value }: { entityId: string; value: boolean }) =>
      setExtendedVisibility(entityId, value, 'I understand'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['aggregator-grants', projectId] })
      setExtVisConfirmTarget(null)
    },
  })

  const grants = data?.grants ?? []
  const actionFailed =
    revokeMutation.isError || restoreFolderMutation.isError || extVisMutation.isError

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">Aggregator Access</h2>
        <button
          onClick={() => setGrantDialogOpen(true)}
          className="flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700"
          data-testid="grant-access-btn"
        >
          <Plus className="h-4 w-4" />
          Grant Access
        </button>
      </div>

      {actionFailed && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700" data-testid="grants-action-error">
          An action failed — please try again.
        </div>
      )}

      {isLoading && (
        <div className="text-sm text-gray-500" data-testid="grants-loading">
          Loading aggregator access…
        </div>
      )}

      {!isLoading && isError && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700" data-testid="grants-error">
          Could not load aggregator access.{' '}
          <button onClick={() => refetch()} className="font-medium underline">Retry</button>
        </div>
      )}

      {!isLoading && !isError && grants.length === 0 && (
        <div className="rounded-lg border border-dashed border-gray-300 p-8 text-center text-sm text-gray-500" data-testid="grants-empty">
          No aggregators have access to this project.
        </div>
      )}

      <ul className="space-y-3" data-testid="grants-list">
        {grants.map((grant) => {
          const isSponsored = grant.subscription_type === 'sponsored'
          return (
            <li
              key={grant.aggregator_id}
              className="rounded-lg border border-gray-200 bg-white p-4"
              data-testid={`grant-card-${grant.aggregator_id}`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-2">
                  <Shield className="h-5 w-5 text-gray-500 shrink-0" />
                  <div>
                    <span className="font-medium text-gray-900">
                      {grant.aggregator_name ?? grant.aggregator_id}
                    </span>
                    {isSponsored && (
                      <span
                        className="ml-2 inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800"
                        data-testid={`sponsor-badge-${grant.aggregator_id}`}
                      >
                        Sponsored
                      </span>
                    )}
                    <p className="text-xs text-gray-500 mt-0.5">
                      Granted {new Date(grant.created_at).toLocaleDateString('en-AU')}
                      {grant.expires_at && ` · Expires ${new Date(grant.expires_at * 1000).toLocaleDateString('en-AU')}`}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => setHideFolderTarget(grant)}
                    className="flex items-center gap-1 rounded px-2 py-1 text-xs text-gray-600 hover:bg-gray-100"
                    data-testid={`hide-folder-btn-${grant.aggregator_id}`}
                  >
                    <FolderX className="h-3.5 w-3.5" />
                    Hide Folder
                  </button>
                  <button
                    onClick={() => setRevokeTarget(grant)}
                    className="flex items-center gap-1 rounded px-2 py-1 text-xs text-red-600 hover:bg-red-50"
                    data-testid={`revoke-btn-${grant.aggregator_id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    Revoke
                  </button>
                </div>
              </div>

              {isSponsored && grant.entity_id && (
                <div className="mt-3 flex items-center justify-between rounded-md bg-amber-50 px-3 py-2">
                  <div className="flex items-center gap-2 text-sm text-amber-800">
                    <Eye className="h-4 w-4" />
                    <span>Extended visibility</span>
                  </div>
                  <button
                    onClick={() =>
                      setExtVisConfirmTarget({ grant, value: !grant.extended_visibility })
                    }
                    className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none ${
                      grant.extended_visibility ? 'bg-amber-500' : 'bg-gray-200'
                    }`}
                    role="switch"
                    aria-checked={grant.extended_visibility ?? false}
                    data-testid={`ext-vis-toggle-${grant.aggregator_id}`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition-transform ${
                        grant.extended_visibility ? 'translate-x-4' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>
              )}

              {grant.exclusions.length > 0 && (
                <div className="mt-3 space-y-1.5">
                  <p className="text-xs font-medium text-gray-600">Hidden folders:</p>
                  {grant.exclusions.map((excl) => (
                    <div
                      key={excl.folder_id}
                      className="flex items-center justify-between rounded bg-gray-50 px-2 py-1"
                      data-testid={`exclusion-${grant.aggregator_id}-${excl.folder_id}`}
                    >
                      <span className="text-xs text-gray-700">{excl.folder_name ?? excl.folder_id}</span>
                      <button
                        onClick={() =>
                          restoreFolderMutation.mutate({
                            aggregatorId: grant.aggregator_id,
                            folderId: excl.folder_id,
                          })
                        }
                        className="text-xs text-blue-600 hover:underline"
                        data-testid={`restore-folder-btn-${grant.aggregator_id}-${excl.folder_id}`}
                      >
                        Restore
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </li>
          )
        })}
      </ul>

      <div data-testid="grant-dialog-placeholder">
        <GrantAggregatorAccessDialog
          projectId={projectId}
          isOpen={grantDialogOpen}
          onClose={() => setGrantDialogOpen(false)}
          onSuccess={() => setGrantDialogOpen(false)}
        />
      </div>

      <div data-testid="hide-folder-placeholder">
        {hideFolderTarget && (
          <HideFolderPicker
            projectId={projectId}
            aggregatorId={hideFolderTarget.aggregator_id}
            isOpen={hideFolderTarget !== null}
            onClose={() => setHideFolderTarget(null)}
            onSuccess={() => setHideFolderTarget(null)}
          />
        )}
      </div>

      <ConfirmDialog
        open={revokeTarget !== null}
        title="Revoke aggregator access?"
        message={`Remove ${revokeTarget?.aggregator_name ?? revokeTarget?.aggregator_id ?? 'this aggregator'}'s access to this project? They will no longer be able to view project data.`}
        confirmLabel="Revoke"
        variant="danger"
        onConfirm={() => {
          if (revokeTarget) {
            revokeMutation.mutate({ aggregatorId: revokeTarget.aggregator_id })
          }
        }}
        onCancel={() => setRevokeTarget(null)}
      />

      <ConfirmDialog
        open={extVisConfirmTarget !== null}
        title={extVisConfirmTarget?.value ? 'Enable extended visibility?' : 'Disable extended visibility?'}
        message={
          extVisConfirmTarget?.value
            ? 'This lets the sponsor see additional project metadata, bypassing folder exclusions. Click Enable to confirm.'
            : 'Disabling extended visibility will restrict the sponsor\'s view of this project.'
        }
        confirmLabel={extVisConfirmTarget?.value ? 'Enable' : 'Disable'}
        variant="default"
        onConfirm={() => {
          if (extVisConfirmTarget?.grant.entity_id) {
            extVisMutation.mutate({
              entityId: extVisConfirmTarget.grant.entity_id,
              value: extVisConfirmTarget.value,
            })
          }
        }}
        onCancel={() => setExtVisConfirmTarget(null)}
      />
    </div>
  )
}
